/**
 * "Request timber quotes" — one round, three steps:
 *
 *   Step 1  Scope: one row per structure lane (the timber list that covers it), pick the
 *           version to send, see what was last requested.
 *   Step 2  Suppliers: who gets which lanes; species + dryness from the proposal; delivery.
 *   Step 3  Review & send: label, stage, due date, message, per-supplier preview.
 *
 * The wizard only drafts; `sendRfqRequest` commits (stage → invites → packages → request).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeft, ArrowRight, ChevronDown, ChevronRight, Loader2, Send, X } from 'lucide-react';
import type {
  ProjectFile,
  ProjectFileStream,
  ProposalNodeSet,
  RfqHubFileRelease,
  RfqPackage,
  RfqProject,
  RfqRequest,
  RfqRequestStage,
  SupplierOrg,
  SupplierShipToPreset,
  TimberRfqDrynessSpec,
  TimberSupplierStructureRef,
} from '../../types';
import type { StructureRef } from '../../utils/proposalStructureRefs';
import { headVersionForStream, versionsForStream } from '../../utils/projectFileStreamVersions';
import { timberListVersionOptionLabel } from '../../utils/proposalStructuresFromTimberLists';
import { PROJECT_FILE_DETAIL_STAGE_LABELS, type ProjectFileDetailStage } from '../../utils/projectFileVersionGroups';
import {
  RFQ_REQUEST_STAGE_HINTS,
  RFQ_REQUEST_STAGE_LABELS,
  RFQ_REQUEST_STAGES,
  rfqRequestStageForDetailStage,
} from '../../utils/rfqRequestStage';
import { buildRfqPublishEmailHtml, rfqRequestRoundEmailSubject } from '../../utils/rfqPublishEmailPreview';
import { speciesForStructure, structureSpeciesRulesFromInvite } from '../../utils/timberRfqStructureSpecies';
import { coverageLaneSummary, type RfqCoverageLane } from '../../utils/rfqRequestCoverage';
import { unsentStagedReleases } from '../../utils/rfqReleaseGroups';
import { defaultRfqRequestLabel, type RfqRequestLineDraft, type SendRfqRequestInput } from '../../utils/rfqRequests';
import { rfqProjectSupplierOrgIds } from '../../utils/rfqProjectSuppliers';
import { buildTimberSupplierInvites } from '../../utils/timberRfqSuppliers';
import { proposalSpeciesDrynessForInvite, type ProposalSpeciesSelectionSource } from '../../utils/timberRfqStructureScope';
import { nodeSetsFromTimberCsvText } from '../../utils/rfqHubTimberImport';
import {
  buildTimberSupplierCsvText,
  prepareTimberSupplierCsvText,
  timberRfqEmailAttachmentFilename,
  type TimberSupplierExportOptions,
} from '../../utils/supplierTimberExport';
import TimberCsvPreviewTable from '../vendor/TimberCsvPreviewTable';
import {
  buildShipToByOrgId,
  inviteShipToConfig,
  shipToContextForWorkType,
  type ProjectSiteAddressInput,
  type SupplierShipToConfig,
} from '../../utils/supplierShipTo';
import { normalizeTimberRfqDrynessSpec, timberRfqDrynessSpecLabel } from '../../utils/supplierTimberExport';
import {
  normalizeNotifyEmails,
  resolveSupplierRfqRecipientEmails,
  supplierRfqRecipientCandidates,
} from '../../utils/supplierRfqRecipients';
import { useCRM } from '../../utils/CRMContext';
import { normalizeSupplierOrg, orgHasCapability } from '../../utils/vendorHelpers';
import SupplierRecipientPicker from '../vendor/SupplierRecipientPicker';
import SupplierShipToFields from '../vendor/SupplierShipToFields';
import './ProjectFiles.css';
import './SplitTracksModal.css';
import './CreateStructuresFromTimberListsModal.css';
import './RequestTimberQuotesWizard.css';

export type RequestTimberQuotesDraft = Omit<SendRfqRequestInput, 'actor' | 'existingProject' | 'existingReleases' | 'existingPackages' | 'proposalNodeSets'>;

export interface RequestTimberQuotesWizardProps {
  dealId: string;
  projectId: string;
  dealTitle?: string;
  timberProjectName?: string;
  streams: ProjectFileStream[];
  files: ProjectFile[];
  structures: StructureRef[];
  lanes: RfqCoverageLane[];
  supplierOrgs: SupplierOrg[];
  existingProject: RfqProject | null;
  cncProject?: RfqProject | null;
  existingReleases: RfqHubFileRelease[];
  existingPackages: RfqPackage[];
  /** Rounds already sent — releases on them are never treated as "staged, not sent". */
  existingRequests?: RfqRequest[];
  proposalNodeSets: ProposalNodeSet[] | null;
  proposalSpeciesSource: ProposalSpeciesSelectionSource | null;
  projectSite: ProjectSiteAddressInput;
  /** Preselect one lane (opened from a track header). */
  initialStreamId?: string | null;
  /** Preselect several lanes by track (re-request a whole round from Files → RFQs). */
  initialStreamIds?: string[] | null;
  /** Preselect the lanes behind these staged releases (hand-off from the Suppliers tab). */
  initialReleaseIds?: string[] | null;
  busy?: boolean;
  onCancel: () => void;
  onSend: (draft: RequestTimberQuotesDraft) => void;
}

interface ScopeRow {
  key: string;
  lane: RfqCoverageLane;
  included: boolean;
  fileId: string;
  displayLabel: string;
  /** Newest staged-but-unsent release on this lane's track, when one exists. */
  staged: RfqHubFileRelease | null;
}

/** Most common detail stage among the given files (ties → first seen). */
function dominantStage(files: ReadonlyArray<ProjectFile | undefined>): ProjectFileDetailStage | '' {
  const counts = new Map<ProjectFileDetailStage, number>();
  for (const f of files) {
    const st = f?.detailStage;
    if (st) counts.set(st, (counts.get(st) ?? 0) + 1);
  }
  let best: ProjectFileDetailStage | '' = '';
  let bestN = 0;
  counts.forEach((n, st) => {
    if (n > bestN) {
      best = st;
      bestN = n;
    }
  });
  return best;
}

const todayPlus = (days: number) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

const RequestTimberQuotesWizard: React.FC<RequestTimberQuotesWizardProps> = ({
  dealId,
  projectId,
  dealTitle,
  timberProjectName,
  files,
  lanes,
  supplierOrgs,
  existingProject,
  cncProject = null,
  existingReleases,
  existingPackages,
  existingRequests,
  proposalNodeSets,
  proposalSpeciesSource,
  projectSite,
  initialStreamId = null,
  initialStreamIds = null,
  initialReleaseIds = null,
  busy = false,
  onCancel,
  onSend,
}) => {
  const { crmUsers, supplierInvites } = useCRM();
  const [step, setStep] = useState<1 | 2 | 3>(1);

  // ── Step 1: scope rows ──
  const requestable = useMemo(() => lanes.filter(l => l.stream?.id && l.head), [lanes]);
  const [rows, setRows] = useState<ScopeRow[]>(() => {
    // Lists staff already staged by hand (Suppliers tab) but never sent: default to that
    // version + name so the wizard picks up where staging left off.
    const unsent = unsentStagedReleases({
      releases: existingReleases.filter(r => r.workType === 'timber_rfq'),
      packages: existingPackages,
      requests: existingRequests,
    });
    const stagedFor = (lane: RfqCoverageLane): RfqHubFileRelease | null => {
      const streamId = lane.stream?.id;
      if (!streamId) return null;
      const versionIds = new Set(versionsForStream(lane.stream, files).map(v => v.id));
      const candidates = unsent.filter(r => r.sourceStreamId === streamId && versionIds.has(r.sourceFileId));
      return candidates.sort((a, b) => b.releaseVersionIndex - a.releaseVersionIndex)[0] ?? null;
    };
    const wanted = initialReleaseIds?.length ? new Set(initialReleaseIds) : null;
    const wantedStreams = new Set([...(initialStreamIds ?? []), ...(initialStreamId ? [initialStreamId] : [])]);
    const anyNew = requestable.some(l => l.state !== 'requested');
    // Staged names that don't say which structure they are ("Timber List - Concept Only"
    // ×3) get the lane prefixed so suppliers can tell the lists apart.
    const stagedLabelFor = (l: RfqCoverageLane, staged: RfqHubFileRelease | null): string | null => {
      const raw = staged?.displayLabel?.trim();
      if (!raw) return null;
      if (l.key === '__whole_project__') return raw;
      const lower = raw.toLowerCase();
      const namesStructure = l.structureNames.some(n => n && lower.includes(n.toLowerCase()));
      return namesStructure ? raw : `${l.label} — ${raw}`;
    };
    return requestable.map(l => {
      const staged = stagedFor(l);
      const included = wanted
        ? !!staged?.id && wanted.has(staged.id)
        : wantedStreams.size
          ? wantedStreams.has(l.stream!.id!)
          : anyNew
            ? l.state !== 'requested'
            : true;
      return {
        key: l.key,
        lane: l,
        included,
        fileId: staged?.sourceFileId ?? l.head!.id!,
        displayLabel: stagedLabelFor(l, staged) || (l.key === '__whole_project__' ? 'Timber list' : `${l.label} timber list`),
        staged,
      };
    });
  });
  const updateRow = (key: string, fn: (r: ScopeRow) => ScopeRow) => setRows(prev => prev.map(r => (r.key === key ? fn(r) : r)));
  const includedRows = rows.filter(r => r.included);
  const versionsFor = (lane: RfqCoverageLane) => versionsForStream(lane.stream, files);
  const fileFor = (row: ScopeRow) => files.find(f => f.id === row.fileId) ?? (row.lane.stream ? headVersionForStream(row.lane.stream, files) : undefined);
  const step1Problem = includedRows.length === 0 ? 'Pick at least one timber list to send.' : includedRows.some(r => !r.displayLabel.trim()) ? 'Every list needs a name suppliers will see.' : null;

  /** Species on one structure's list, from the proposal (empty when the proposal has no data for it). */
  const speciesForStructures = (structures: TimberSupplierStructureRef[]): string[] => {
    if (!proposalNodeSets?.length) return [];
    const scope = structures.length ? { structureScope: 'selected' as const, structures } : undefined;
    return proposalSpeciesDrynessForInvite(proposalSpeciesSource, proposalNodeSets, scope).map(e => e.species);
  };
  const lines: RfqRequestLineDraft[] = includedRows.map(r => {
    const f = fileFor(r);
    const structures = r.lane.structureIds.map((id, i) => ({ id, name: r.lane.structureNames[i] || id }));
    const species = speciesForStructures(structures);
    return {
      key: r.key,
      structures,
      sourceStreamId: r.lane.stream!.id!,
      sourceFileId: f?.id ?? r.fileId,
      versionIndex: f?.versionIndex,
      versionLabel: f?.versionLabel,
      displayLabel: r.displayLabel.trim(),
      ...(f?.trueDimensions ? { trueDimensions: true as const } : {}),
      ...(species.length ? { species } : {}),
    };
  });

  // ── Step 2: suppliers ──
  const timberOrgs = useMemo(
    () => supplierOrgs.filter(o => o.id && orgHasCapability(normalizeSupplierOrg(o), 'timber')).sort((a, b) => a.name.localeCompare(b.name)),
    [supplierOrgs],
  );
  const roster = useMemo(() => (existingProject ? rfqProjectSupplierOrgIds(existingProject) : []), [existingProject]);
  const [selectedOrgIds, setSelectedOrgIds] = useState<string[]>(() => roster.filter(id => timberOrgs.some(o => o.id === id)));
  /** org → line keys it receives; absent = all. */
  const [lineKeysByOrg, setLineKeysByOrg] = useState<Record<string, string[]>>({});
  const [drynessByOrg, setDrynessByOrg] = useState<Record<string, Record<string, TimberRfqDrynessSpec>>>({});
  const [extraSpeciesByOrg, setExtraSpeciesByOrg] = useState<Record<string, string>>({});
  /** org → structure id → species staff ticked for it; absent = proposal default for that structure. */
  const [structureSpeciesByOrg, setStructureSpeciesByOrg] = useState<Record<string, Record<string, string[]>>>({});
  /** org → recipient emails; absent = everyone on the existing invite (or all contacts). */
  const [notifyEmailsByOrg, setNotifyEmailsByOrg] = useState<Record<string, string[]>>({});
  const [shipToByOrg, setShipToByOrg] = useState<Record<string, SupplierShipToConfig>>({});
  const shipCtx = useMemo(() => shipToContextForWorkType('timber_rfq', existingProject, cncProject), [existingProject, cncProject]);

  const toggleOrg = (id: string) =>
    setSelectedOrgIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  const orgLineKeys = (orgId: string): string[] => lineKeysByOrg[orgId] ?? lines.map(l => l.key);
  const toggleOrgLine = (orgId: string, key: string) =>
    setLineKeysByOrg(prev => {
      const cur = prev[orgId] ?? lines.map(l => l.key);
      const next = cur.includes(key) ? cur.filter(k => k !== key) : [...cur, key];
      return { ...prev, [orgId]: next };
    });
  const structuresForOrg = (orgId: string): TimberSupplierStructureRef[] => {
    const keys = new Set(orgLineKeys(orgId));
    return lines.filter(l => keys.has(l.key)).flatMap(l => l.structures);
  };
  const speciesForOrg = (orgId: string) => {
    const structures = structuresForOrg(orgId);
    const coversAll = lines.filter(l => orgLineKeys(orgId).includes(l.key)).some(l => l.structures.length === 0);
    const scope = structures.length && !coversAll ? { structureScope: 'selected' as const, structures } : undefined;
    const fromProposal = proposalNodeSets?.length ? proposalSpeciesDrynessForInvite(proposalSpeciesSource, proposalNodeSets, scope) : [];
    const existingInvite = existingProject?.timberSupplierInvites?.find(i => i.supplierOrgId === orgId);
    const base = fromProposal.length
      ? fromProposal
      : (existingInvite?.requestedSpecies ?? []).map(sp => ({
          species: sp,
          drynessSpec: normalizeTimberRfqDrynessSpec(existingInvite?.speciesDryness?.[sp] ?? existingInvite?.drynessSpec),
        }));
    const extra = (extraSpeciesByOrg[orgId] ?? '')
      .split(',')
      .map(s => s.trim())
      .filter(Boolean)
      .filter(sp => !base.some(b => b.species.toLowerCase() === sp.toLowerCase()))
      .map(sp => ({ species: sp, drynessSpec: 'kiln_dried' as const }));
    return [...base, ...extra].map(e => ({ ...e, drynessSpec: normalizeTimberRfqDrynessSpec(drynessByOrg[orgId]?.[e.species] ?? e.drynessSpec) }));
  };
  const shipToFor = (org: SupplierOrg): SupplierShipToConfig =>
    shipToByOrg[org.id!] ?? inviteShipToConfig(existingProject?.timberSupplierInvites?.find(i => i.supplierOrgId === org.id), { ...shipCtx, orgId: org.id! });

  // ── Species by structure ──
  /** Structures this org quotes, deduped by id (a lane may cover several). */
  const structureRowsForOrg = (orgId: string): TimberSupplierStructureRef[] => {
    const seen = new Set<string>();
    return structuresForOrg(orgId).filter(st => (seen.has(st.id) ? false : (seen.add(st.id), true)));
  };
  /**
   * Default species for one structure: what the proposal lists for it (within the org's
   * species pool) plus any species the proposal names on none of the org's structures
   * (manual additions / legacy invite species). No proposal data → every species.
   */
  const defaultSpeciesOnStructure = (orgId: string, st: TimberSupplierStructureRef): string[] => {
    const pool = speciesForOrg(orgId).map(e => e.species);
    const rows = structureRowsForOrg(orgId);
    const fromProposal = (s: TimberSupplierStructureRef) => speciesForStructures([s]).map(sp => sp.toLowerCase());
    const mine = new Set(fromProposal(st));
    if (!mine.size) return pool;
    const anywhere = new Set(rows.flatMap(fromProposal));
    return pool.filter(sp => mine.has(sp.toLowerCase()) || !anywhere.has(sp.toLowerCase()));
  };
  const speciesOnStructure = (orgId: string, st: TimberSupplierStructureRef): string[] => {
    const pool = speciesForOrg(orgId).map(e => e.species);
    const picked = structureSpeciesByOrg[orgId]?.[st.id];
    if (!picked) return defaultSpeciesOnStructure(orgId, st);
    return pool.filter(sp => picked.some(p => p.toLowerCase() === sp.toLowerCase()));
  };
  const toggleStructureSpecies = (orgId: string, st: TimberSupplierStructureRef, species: string) => {
    const cur = speciesOnStructure(orgId, st);
    const next = cur.some(sp => sp.toLowerCase() === species.toLowerCase())
      ? cur.filter(sp => sp.toLowerCase() !== species.toLowerCase())
      : [...cur, species];
    setStructureSpeciesByOrg(prev => ({ ...prev, [orgId]: { ...(prev[orgId] ?? {}), [st.id]: next } }));
  };
  /** Full structure → species map for the invite (normalized later: all-species entries drop out). */
  const structureSpeciesMapForOrg = (orgId: string): Record<string, string[]> | null => {
    const rows = structureRowsForOrg(orgId);
    if (!rows.length) return null;
    return Object.fromEntries(rows.map(st => [st.id, speciesOnStructure(orgId, st)]));
  };

  // ── Recipients ──
  const recipientCandidatesFor = (orgId: string) =>
    supplierRfqRecipientCandidates(timberOrgs.find(o => o.id === orgId), supplierInvites, crmUsers);
  /** Current selection for the picker: draft, else the existing invite's list, else everyone. */
  const notifyEmailsFor = (orgId: string): string[] | null => {
    const draft = notifyEmailsByOrg[orgId];
    if (draft) return draft;
    const existingInvite = existingProject?.timberSupplierInvites?.find(i => i.supplierOrgId === orgId);
    return existingInvite?.notifyEmails?.length ? existingInvite.notifyEmails : null;
  };
  const recipientEmailsFor = (orgId: string): string[] =>
    resolveSupplierRfqRecipientEmails(recipientCandidatesFor(orgId), { notifyEmails: notifyEmailsFor(orgId) ?? undefined });

  const unpricedStructure = selectedOrgIds
    .flatMap(id => structureRowsForOrg(id).filter(st => speciesOnStructure(id, st).length === 0).map(st => ({ id, st })))
    .find(Boolean);
  const step2Problem =
    selectedOrgIds.length === 0
      ? 'Pick at least one supplier.'
      : selectedOrgIds.some(id => orgLineKeys(id).length === 0)
        ? 'Every supplier needs at least one list to quote.'
        : selectedOrgIds.some(id => speciesForOrg(id).length === 0)
          ? 'Add at least one species for each supplier.'
          : unpricedStructure
            ? `Pick at least one species for ${unpricedStructure.st.name} (${timberOrgs.find(o => o.id === unpricedStructure.id)?.name ?? 'supplier'}) or untick that list.`
            : selectedOrgIds.some(id => recipientCandidatesFor(id).length > 0 && recipientEmailsFor(id).length === 0)
              ? 'Pick at least one recipient for each supplier.'
              : null;

  // ── Step 3: review ──
  const includedStages = useMemo(
    () => Array.from(new Set(includedRows.map(r => fileFor(r)?.detailStage).filter((s): s is ProjectFileDetailStage => !!s))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, files],
  );
  // Concept vs Production: defaulted from the lists' LOD (estimating / conceptual →
  // Concept, joinery and later → Production); staff can flip it before sending.
  const defaultStage = useMemo<RfqRequestStage>(
    () => rfqRequestStageForDetailStage(dominantStage(includedRows.map(r => fileFor(r))) || undefined),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, files],
  );
  const [stage, setStage] = useState<RfqRequestStage>(defaultStage);
  const [stageEdited, setStageEdited] = useState(false);
  // Concept / Production is shown in the email body and the portal chip, not in the label.
  const [label, setLabel] = useState(() => defaultRfqRequestLabel(undefined));
  useEffect(() => {
    if (!stageEdited) setStage(defaultStage);
  }, [defaultStage, stageEdited]);
  const [dueDate, setDueDate] = useState<string>(() => todayPlus(14));
  const [message, setMessage] = useState('');
  const step3Problem = !label.trim() ? 'Give the request a label.' : null;

  /** Invites + resolved ship-to for the selected suppliers (shared by send and the email preview). */
  const buildInvitesAndShipTo = () => {
    const invites = buildTimberSupplierInvites(
      selectedOrgIds,
      Object.fromEntries(selectedOrgIds.map(id => [id, speciesForOrg(id).map(e => e.species)])),
      existingProject?.timberSupplierInvites,
      Object.fromEntries(selectedOrgIds.map(id => [id, Object.fromEntries(speciesForOrg(id).map(e => [e.species, e.drynessSpec]))])),
      existingProject?.timberDrynessSpec,
      Object.fromEntries(selectedOrgIds.map(id => [id, shipToFor(timberOrgs.find(o => o.id === id)!)])),
      Object.fromEntries(
        selectedOrgIds.map(id => {
          const structures = structuresForOrg(id);
          const coversAll = lines.filter(l => orgLineKeys(id).includes(l.key)).some(l => l.structures.length === 0);
          return [id, structures.length && !coversAll ? { scope: 'selected' as const, structures } : { scope: 'all' as const, structures: [] }];
        }),
      ),
      undefined,
      Object.fromEntries(selectedOrgIds.map(id => [id, structureSpeciesMapForOrg(id)])),
      Object.fromEntries(
        selectedOrgIds.map(id => [id, normalizeNotifyEmails(notifyEmailsFor(id), recipientCandidatesFor(id)) ?? null]),
      ),
    );
    const projectForShipTo = { timberSupplierInvites: invites };
    const shipToByOrgId = buildShipToByOrgId(projectForShipTo, selectedOrgIds, { ...shipCtx, projectSite });
    const shipToPresetByOrgId = Object.fromEntries(
      selectedOrgIds.map(id => [id, inviteShipToConfig(invites.find(i => i.supplierOrgId === id), { ...shipCtx, orgId: id }).preset]),
    ) as Record<string, SupplierShipToPreset>;
    return { invites, shipToByOrgId, shipToPresetByOrgId };
  };

  // ── Email preview (step 3): exactly what one supplier will receive ──
  const [emailPreviewOpen, setEmailPreviewOpen] = useState(false);
  const [emailPreviewOrgId, setEmailPreviewOrgId] = useState<string | null>(null);
  const previewOrgId = emailPreviewOrgId && selectedOrgIds.includes(emailPreviewOrgId) ? emailPreviewOrgId : selectedOrgIds[0] ?? null;
  const emailPreview = useMemo(() => {
    if (!emailPreviewOpen || !previewOrgId) return null;
    const org = timberOrgs.find(o => o.id === previewOrgId);
    const keys = new Set(orgLineKeys(previewOrgId));
    const got = lines.filter(l => keys.has(l.key));
    const species = speciesForOrg(previewOrgId);
    const { invites, shipToByOrgId } = buildInvitesAndShipTo();
    // A supplier whose invite narrows species per structure sees that narrowing here too.
    const invite = invites.find(i => i.supplierOrgId === previewOrgId);
    const speciesRules = structureSpeciesRulesFromInvite(invite, got.flatMap(l => l.structures));
    const lineSpecies = (l: RfqRequestLineDraft): string[] | undefined => {
      if (!speciesRules.length || !l.structures.length) return l.species;
      const narrowed = Array.from(new Set(l.structures.flatMap(st => speciesForStructure(st, invite?.requestedSpecies ?? [], speciesRules))));
      return narrowed;
    };
    const due = dueDate ? new Date(`${dueDate}T17:00:00`) : null;
    const structureNames = Array.from(new Set(got.flatMap(l => l.structures.map(st => st.name)).filter(Boolean)));
    const html = buildRfqPublishEmailHtml({
      recipientFirstName: org?.defaultContactFirstName?.trim().split(/\s+/)[0] || undefined,
      vendorOrgName: org?.name,
      dealTitle: dealTitle || 'Project',
      projectName: dealTitle,
      requestedSpecies: species.map(e => e.species),
      speciesDryness: species,
      workType: 'timber_rfq',
      customMessage: message.trim() || undefined,
      timberStructures: structureNames.length ? structureNames : undefined,
      shipToAddress: shipToByOrgId[previewOrgId],
      requestRound: {
        label: label.trim() || 'Timber quotes',
        stageLabel: RFQ_REQUEST_STAGE_LABELS[stage],
        dueDateText: due
          ? due.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
          : undefined,
        lines: got.map(l => ({
          structures: l.structures.map(st => st.name),
          displayLabel: l.displayLabel || 'Timber list',
          versionLabel: l.versionLabel,
          versionIndex: l.versionIndex,
          trueDimensions: l.trueDimensions === true,
          species: lineSpecies(l),
          structureSpecies: l.structures.map(st => ({
            name: st.name,
            species: speciesOnStructure(previewOrgId, st),
          })),
        })),
      },
    });
    const exportOptions: TimberSupplierExportOptions | undefined = species.length
      ? {
          requestedSpeciesEntries: species,
          ...(speciesRules.length ? { structureSpecies: speciesRules } : {}),
        }
      : undefined;
    return {
      html,
      subject: rfqRequestRoundEmailSubject(undefined, dealTitle, label.trim()),
      orgName: org?.name ?? 'Supplier',
      toName: org?.defaultContactFirstName?.trim() || undefined,
      toEmail: recipientEmailsFor(previewOrgId).join(', ') || undefined,
      csvCount: got.length,
      exportOptions,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailPreviewOpen, previewOrgId, lines, lineKeysByOrg, label, stage, dueDate, message, timberOrgs, shipToByOrg, extraSpeciesByOrg, drynessByOrg, structureSpeciesByOrg, notifyEmailsByOrg, crmUsers, supplierInvites]);

  /**
   * One CSV per structure line, the same files the request email attaches.
   * Signature is content, not array identity — `lines` is rebuilt every render.
   */
  type CsvPreviewRequest = {
    key: string;
    fileName: string;
    url: string;
    structures: TimberSupplierStructureRef[];
    trueDimensions: boolean;
  };
  const csvPreviewRequests: CsvPreviewRequest[] = !emailPreviewOpen || !previewOrgId
    ? []
    : lines
        .filter(l => (lineKeysByOrg[previewOrgId] ?? lines.map(x => x.key)).includes(l.key))
        .map(l => {
          const file = files.find(f => f.id === l.sourceFileId);
          const release = existingReleases.find(
            r => r.workType === 'timber_rfq' && r.sourceFileId === l.sourceFileId && !!r.releasedUrl,
          );
          const name = l.displayLabel.trim() || file?.name || 'timber-list';
          return {
            key: l.key,
            fileName: /\.csv$/i.test(name) ? name : `${name}.csv`,
            url: (release?.releasedUrl || file?.url || '').trim(),
            structures: l.structures,
            trueDimensions: file?.trueDimensions === true,
          };
        });
  const csvPreviewSignature = `${proposalNodeSets?.length ?? 0}:${JSON.stringify(
    csvPreviewRequests.map(l => ({
      key: l.key,
      fileName: l.fileName,
      url: l.url,
      structures: l.structures,
      trueDimensions: l.trueDimensions,
    })),
  )}`;
  const csvPreviewRequestsRef = useRef(csvPreviewRequests);
  csvPreviewRequestsRef.current = csvPreviewRequests;
  const proposalNodeSetsRef = useRef(proposalNodeSets);
  proposalNodeSetsRef.current = proposalNodeSets;
  type LineCsvPreview = {
    key: string;
    fileName: string;
    nodeSets: ProposalNodeSet[];
    /** Original file text, used when the email attaches the staged CSV instead of a snapshot. */
    rawCsvText?: string;
    /** True when structure names don't match the snapshot, so the email sends the staged file. */
    useRawFile: boolean;
    trueDimensions?: boolean;
    error?: string;
  };
  const [csvPreviews, setCsvPreviews] = useState<LineCsvPreview[]>([]);
  const [csvPreviewLoading, setCsvPreviewLoading] = useState(false);
  const parsedCsvCache = useRef(new Map<string, { nodeSets: ProposalNodeSet[]; rawCsvText: string; error?: string }>());

  useEffect(() => {
    const rowsToLoad = csvPreviewRequestsRef.current;
    if (!rowsToLoad.length) {
      setCsvPreviews([]);
      setCsvPreviewLoading(false);
      return;
    }
    const proposalCount = proposalNodeSetsRef.current?.length ?? 0;
    let cancelled = false;
    setCsvPreviewLoading(true);
    void (async () => {
      const out: LineCsvPreview[] = [];
      for (const line of rowsToLoad) {
        if (!line.url) {
          out.push({ key: line.key, fileName: line.fileName, nodeSets: [], useRawFile: false, error: 'This timber list has no file to preview.' });
          continue;
        }
        const cacheKey = `${line.url}\n${proposalCount}\n${line.trueDimensions ? '1' : '0'}`;
        let parsed = parsedCsvCache.current.get(cacheKey);
        if (!parsed) {
          try {
            const text = await fetch(line.url).then(r => {
              if (!r.ok) throw new Error('Could not load the timber list.');
              return r.text();
            });
            const built = nodeSetsFromTimberCsvText(text, proposalNodeSetsRef.current, {
              trueDimensions: line.trueDimensions,
            });
            parsed = built.nodeSets.length
              ? { nodeSets: built.nodeSets, rawCsvText: text }
              : { nodeSets: [], rawCsvText: text, error: built.parseErrors[0] || 'No timber rows parsed from CSV.' };
          } catch (err) {
            parsed = { nodeSets: [], rawCsvText: '', error: err instanceof Error ? err.message : 'Could not preview this CSV.' };
          }
          parsedCsvCache.current.set(cacheKey, parsed);
        }
        if (parsed.error) {
          out.push({
            key: line.key,
            fileName: line.fileName,
            nodeSets: [],
            rawCsvText: parsed.rawCsvText,
            useRawFile: !!parsed.rawCsvText,
            trueDimensions: line.trueDimensions,
            ...(parsed.rawCsvText ? {} : { error: parsed.error }),
          });
          continue;
        }
        // The request email attaches this list's whole CSV. Structure names on the
        // quote do not drop rows; suppliers file their upload by structure group.
        out.push({
          key: line.key,
          fileName: line.fileName,
          nodeSets: parsed.nodeSets,
          rawCsvText: parsed.rawCsvText,
          useRawFile: parsed.nodeSets.length === 0,
          trueDimensions: line.trueDimensions,
        });
      }
      if (!cancelled) {
        setCsvPreviews(out);
        setCsvPreviewLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [csvPreviewSignature]);

  const send = () => {
    const { invites, shipToByOrgId, shipToPresetByOrgId } = buildInvitesAndShipTo();
    const trimmedKeys: Record<string, string[]> = {};
    for (const id of selectedOrgIds) {
      const keys = lineKeysByOrg[id];
      if (keys && keys.length !== lines.length) trimmedKeys[id] = keys.filter(k => lines.some(l => l.key === k));
    }
    onSend({
      dealId,
      projectId,
      dealTitle,
      timberProjectName,
      label: label.trim(),
      stage,
      message: message.trim() || undefined,
      quoteDeadline: dueDate ? new Date(`${dueDate}T17:00:00`) : undefined,
      lines,
      supplierOrgIds: selectedOrgIds,
      lineKeysByOrg: Object.keys(trimmedKeys).length ? trimmedKeys : undefined,
      invites,
      shipToByOrgId,
      shipToPresetByOrgId,
    });
  };

  // ── Render ──
  const renderStep1 = () => (
    <>
      <p className="pf-split-intro">
        Each row is a timber list for one structure or a structure group. Pick the version suppliers should quote — the newest
        is selected. Lists that were already requested at this version start unchecked.
      </p>
      {requestable.length === 0 ? (
        <p className="pf-split-problem">No timber list has been uploaded to this project yet.</p>
      ) : (
        <ul className="pf-split-track-list rtq-scope-list">
          {rows.map(r => {
            const versions = versionsFor(r.lane);
            const summary = coverageLaneSummary(r.lane);
            const selectedFile = fileFor(r);
            const lod = selectedFile?.detailStage ? PROJECT_FILE_DETAIL_STAGE_LABELS[selectedFile.detailStage] : null;
            const isStagedPick = !!r.staged && r.staged.sourceFileId === (selectedFile?.id ?? r.fileId);
            return (
              <li key={r.key} className={`rtq-scope-card${r.included ? '' : ' rtq-scope-card--off'}`}>
                <label className="rtq-scope-head">
                  <input
                    type="checkbox"
                    aria-label={`Include ${r.lane.label}`}
                    checked={r.included}
                    disabled={busy}
                    onChange={() => updateRow(r.key, cur => ({ ...cur, included: !cur.included }))}
                  />
                  <span className="rtq-scope-copy">
                    <span className="rtq-scope-title">{r.lane.label}</span>
                    <span className="rtq-scope-meta">
                      {r.lane.stream?.title} · {summary}
                    </span>
                  </span>
                  {lod || isStagedPick || selectedFile?.trueDimensions ? (
                    <span className="rtq-scope-badges">
                      {selectedFile?.trueDimensions ? (
                        <span
                          className="rtq-pill rtq-pill--true-dim"
                          title="True timber dimensions. Cross-section stays as written in the CSV (7.5 stays 7.5)."
                        >
                          True dimensions
                        </span>
                      ) : null}
                      {lod ? <span className="rtq-pill rtq-pill--lod">{lod}</span> : null}
                      {isStagedPick ? <span className="rtq-pill rtq-pill--staged">Staged · not sent</span> : null}
                    </span>
                  ) : null}
                </label>
                <div className="rtq-scope-fields">
                  <label className="pf-split-field rtq-version-field">
                    <span className="pf-vr-mu">Version</span>
                    <select
                      className="pf-tag-select pf-text-input-height"
                      value={r.fileId}
                      title={selectedFile ? timberListVersionOptionLabel(selectedFile) : undefined}
                      disabled={busy || !r.included}
                      onChange={e => updateRow(r.key, cur => ({ ...cur, fileId: e.target.value }))}
                    >
                      {versions.map(v =>
                        v.id ? (
                          <option key={v.id} value={v.id}>
                            {timberListVersionOptionLabel(v)}
                          </option>
                        ) : null,
                      )}
                    </select>
                  </label>
                  <label className="pf-split-field rtq-label-field">
                    <span className="pf-vr-mu">Name suppliers see</span>
                    <input
                      type="text"
                      className="pf-text-input pf-text-input-height"
                      value={r.displayLabel}
                      title={r.displayLabel}
                      disabled={busy || !r.included}
                      onChange={e => updateRow(r.key, cur => ({ ...cur, displayLabel: e.target.value }))}
                    />
                  </label>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {lanes.some(l => l.state === 'no_list') ? (
        <p className="pf-split-track-meta rtq-note">
          No timber list yet for: {lanes.filter(l => l.state === 'no_list').map(l => l.label).join(', ')}. Upload one on Deliverables to include it.
        </p>
      ) : null}
      {step1Problem ? <p className="pf-split-problem">{step1Problem}</p> : null}
    </>
  );

  const renderStep2 = () => (
    <>
      <p className="pf-split-intro">
        Pick who quotes. Each supplier gets one RFQ for the lists you tick below. A list covers one structure or a structure group. Species and dryness
        come from the proposal — adjust as needed.
      </p>
      <section className="pf-split-section" aria-labelledby="rtq-suppliers-title">
        <h4 className="pf-split-section-title" id="rtq-suppliers-title">
          Suppliers
          <span className="pf-split-section-meta">
            {selectedOrgIds.length} of {timberOrgs.length} selected
          </span>
        </h4>
        {timberOrgs.length === 0 ? <p className="pf-split-problem">No timber suppliers are set up yet (People → Suppliers).</p> : null}
        <ul className="rtq-supplier-list">
          {timberOrgs.map(org => {
            const id = org.id!;
            const on = selectedOrgIds.includes(id);
            const keys = orgLineKeys(id);
            const species = on ? speciesForOrg(id) : [];
            const structureRows = on ? structureRowsForOrg(id) : [];
            return (
              <li key={id} className={`rtq-supplier${on ? ' rtq-supplier--on' : ''}`}>
                <label className="rtq-supplier-head">
                  <input type="checkbox" checked={on} disabled={busy} onChange={() => toggleOrg(id)} />
                  <strong>{org.name}</strong>
                  {roster.includes(id) ? <span className="rtq-pill">on this RFQ</span> : null}
                  {org.defaultContactEmail ? <span className="pf-split-track-meta">{org.defaultContactEmail}</span> : null}
                </label>
                {on ? (
                  <div className="rtq-supplier-body">
                    {lines.length > 1 ? (
                      <div className="rtq-field">
                        <span className="pf-vr-mu">Lists this supplier quotes</span>
                        <div className="rtq-chip-row">
                          {lines.map(l => (
                            <label key={l.key} className={`rtq-chip${keys.includes(l.key) ? ' rtq-chip--on' : ''}`}>
                              <input type="checkbox" checked={keys.includes(l.key)} disabled={busy} onChange={() => toggleOrgLine(id, l.key)} />
                              {l.displayLabel}
                            </label>
                          ))}
                        </div>
                      </div>
                    ) : null}
                    <div className="rtq-field">
                      <span className="pf-vr-mu">Species by structure · dryness</span>
                      {species.length === 0 ? <p className="pf-split-problem">No species found on the proposal for these structures — add one below.</p> : null}
                      {species.length > 0 && structureRows.length > 0 ? (
                        <table className="rtq-structure-species-table">
                          <thead>
                            <tr>
                              <th scope="col">Structure</th>
                              {species.map(e => (
                                <th key={e.species} scope="col">{e.species}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {structureRows.map(st => {
                              const on = speciesOnStructure(id, st);
                              return (
                                <tr key={st.id} className={on.length === 0 ? 'rtq-structure-species-row--none' : ''}>
                                  <th scope="row">
                                    {st.name}
                                    {on.length === 0 ? <span className="rtq-structure-species-none">no species</span> : null}
                                  </th>
                                  {species.map(e => {
                                    const isOn = on.some(sp => sp.toLowerCase() === e.species.toLowerCase());
                                    return (
                                      <td key={e.species}>
                                        <input
                                          type="checkbox"
                                          checked={isOn}
                                          disabled={busy}
                                          aria-label={`${e.species} on ${st.name}`}
                                          onChange={() => toggleStructureSpecies(id, st, e.species)}
                                        />
                                      </td>
                                    );
                                  })}
                                </tr>
                              );
                            })}
                            <tr className="rtq-structure-species-dryness">
                              <th scope="row">Dryness</th>
                              {species.map(e => (
                                <td key={e.species}>
                                  <select
                                    className="pf-tag-select pf-text-input-height"
                                    value={e.drynessSpec}
                                    disabled={busy}
                                    aria-label={`Dryness for ${e.species}`}
                                    onChange={ev =>
                                      setDrynessByOrg(prev => ({
                                        ...prev,
                                        [id]: { ...(prev[id] ?? {}), [e.species]: ev.target.value as TimberRfqDrynessSpec },
                                      }))
                                    }
                                  >
                                    <option value="kiln_dried">{timberRfqDrynessSpecLabel('kiln_dried')}</option>
                                    <option value="green">{timberRfqDrynessSpecLabel('green')}</option>
                                  </select>
                                </td>
                              ))}
                            </tr>
                          </tbody>
                        </table>
                      ) : (
                        <div className="rtq-species-grid">
                          {species.map(e => (
                            <div key={e.species} className="rtq-species">
                              <span>{e.species}</span>
                              <select
                                className="pf-tag-select pf-text-input-height"
                                value={e.drynessSpec}
                                disabled={busy}
                                aria-label={`Dryness for ${e.species}`}
                                onChange={ev =>
                                  setDrynessByOrg(prev => ({
                                    ...prev,
                                    [id]: { ...(prev[id] ?? {}), [e.species]: ev.target.value as TimberRfqDrynessSpec },
                                  }))
                                }
                              >
                                <option value="kiln_dried">{timberRfqDrynessSpecLabel('kiln_dried')}</option>
                                <option value="green">{timberRfqDrynessSpecLabel('green')}</option>
                              </select>
                            </div>
                          ))}
                        </div>
                      )}
                      <input
                        type="text"
                        className="pf-text-input pf-text-input-height rtq-extra-species"
                        placeholder="Add species (comma separated)"
                        value={extraSpeciesByOrg[id] ?? ''}
                        disabled={busy}
                        onChange={ev => setExtraSpeciesByOrg(prev => ({ ...prev, [id]: ev.target.value }))}
                      />
                    </div>
                    <SupplierRecipientPicker
                      orgName={org.name}
                      candidates={recipientCandidatesFor(id)}
                      selected={notifyEmailsFor(id)}
                      disabled={busy}
                      onChange={next => setNotifyEmailsByOrg(prev => ({ ...prev, [id]: next }))}
                    />
                    <SupplierShipToFields
                      org={org}
                      projectSite={projectSite}
                      config={shipToFor(org)}
                      disabled={busy}
                      workType="timber_rfq"
                      timberProject={existingProject}
                      cncProject={cncProject}
                      onChange={cfg => setShipToByOrg(prev => ({ ...prev, [id]: cfg }))}
                    />
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      </section>
      {step2Problem ? <p className="pf-split-problem">{step2Problem}</p> : null}
    </>
  );

  const renderStep3 = () => (
    <>
      <div className="rtq-review-fields">
        <label className="pf-split-field pf-split-field--grow">
          <span className="pf-vr-mu">Request label *</span>
          <input
            type="text"
            className="pf-text-input pf-text-input-height"
            value={label}
            disabled={busy}
            onChange={e => setLabel(e.target.value)}
          />
        </label>
        <div className="pf-split-field" role="radiogroup" aria-labelledby="rtq-stage-label">
          <span className="pf-vr-mu" id="rtq-stage-label">Pricing</span>
          <div className="rtq-stage-toggle">
            {RFQ_REQUEST_STAGES.map(s => (
              <button
                key={s}
                type="button"
                role="radio"
                aria-checked={stage === s}
                className={`rtq-stage-option${stage === s ? ' rtq-stage-option--on' : ''}`}
                disabled={busy}
                title={RFQ_REQUEST_STAGE_HINTS[s]}
                onClick={() => {
                  setStageEdited(true);
                  setStage(s);
                }}
              >
                {RFQ_REQUEST_STAGE_LABELS[s]}
              </button>
            ))}
          </div>
        </div>
        <label className="pf-split-field">
          <span className="pf-vr-mu">Quotes due</span>
          <input type="date" className="pf-text-input pf-text-input-height" value={dueDate} disabled={busy} onChange={e => setDueDate(e.target.value)} />
        </label>
      </div>
      <p className="pf-split-track-meta rtq-note rtq-note--stage">{RFQ_REQUEST_STAGE_HINTS[stage]}</p>
      {includedStages.length > 1 ? (
        <p className="pf-split-track-meta rtq-note">
          These lists are at different levels of detail ({includedStages.map(s => PROJECT_FILE_DETAIL_STAGE_LABELS[s]).join(', ')}). They still go out as one RFQ —
          pick Concept or Production for how suppliers should price it.
        </p>
      ) : null}
      <label className="pf-split-field pf-split-field--grow rtq-message">
        <span className="pf-vr-mu">Message to suppliers (optional)</span>
        <textarea className="pf-text-input" rows={3} value={message} disabled={busy} onChange={e => setMessage(e.target.value)} placeholder="Anything the suppliers should know about this round." />
      </label>
      <section className="pf-split-section" aria-labelledby="rtq-preview-title">
        <h4 className="pf-split-section-title" id="rtq-preview-title">
          Who receives what
          <span className="pf-split-section-meta">one RFQ per supplier · one email, a CSV per list, a quote per list</span>
        </h4>
        <ul className="rtq-preview">
          {selectedOrgIds.map(id => {
            const org = timberOrgs.find(o => o.id === id);
            const keys = new Set(orgLineKeys(id));
            const got = lines.filter(l => keys.has(l.key));
            const species = speciesForOrg(id);
            return (
              <li key={id}>
                <strong>{org?.name ?? 'Supplier'}</strong> gets one {RFQ_REQUEST_STAGE_LABELS[stage]} RFQ covering{' '}
                {got.length === 1 ? '' : `${got.length} lists: `}
                {got.map(l => l.displayLabel).join(', ')}
                <span className="pf-split-track-meta"> · {species.map(e => `${e.species} (${timberRfqDrynessSpecLabel(e.drynessSpec)})`).join(', ')}</span>
              </li>
            );
          })}
        </ul>
      </section>
      <section className="pf-split-section rtq-email-preview" aria-labelledby="rtq-email-title">
        <h4 className="pf-split-section-title" id="rtq-email-title">
          <button type="button" className="rtq-email-toggle" aria-expanded={emailPreviewOpen} onClick={() => setEmailPreviewOpen(v => !v)}>
            {emailPreviewOpen ? <ChevronDown size={13} aria-hidden /> : <ChevronRight size={13} aria-hidden />}
            Email preview
          </button>
          {emailPreviewOpen && selectedOrgIds.length > 1 ? (
            <select
              className="pf-tag-select rtq-email-org"
              aria-label="Preview email for supplier"
              value={previewOrgId ?? ''}
              onChange={e => setEmailPreviewOrgId(e.target.value)}
            >
              {selectedOrgIds.map(id => (
                <option key={id} value={id}>
                  {timberOrgs.find(o => o.id === id)?.name ?? 'Supplier'}
                </option>
              ))}
            </select>
          ) : (
            <span className="pf-split-section-meta">what each supplier's inbox will show</span>
          )}
        </h4>
        {emailPreviewOpen && emailPreview ? (
          <div className="rtq-email-frame-wrap">
            <p className="rtq-email-subject">
              <span className="pf-vr-mu">To</span> <strong>{emailPreview.orgName}</strong>
              {emailPreview.toName ? ` · ${emailPreview.toName}` : ''}
              {emailPreview.toEmail ? (
                <span className="pf-split-track-meta"> · {emailPreview.toEmail}</span>
              ) : (
                <span className="pf-split-track-meta"> · no recipients selected</span>
              )}
            </p>
            <p className="rtq-email-subject">
              <span className="pf-vr-mu">Subject</span> {emailPreview.subject}
              <span className="pf-split-track-meta">
                {' '}· {emailPreview.csvCount} CSV{emailPreview.csvCount === 1 ? '' : 's'} attached{emailPreview.csvCount > 1 ? ' (one per list)' : ''}
              </span>
            </p>
            <iframe className="rtq-email-frame" title={`Email preview for ${emailPreview.orgName}`} srcDoc={emailPreview.html} sandbox="" />
            <div className="rtq-csv-previews">
              {csvPreviewLoading || (csvPreviewRequests.length > 0 && csvPreviews.length === 0) ? (
                <p className="rtq-csv-status">
                  <Loader2 size={14} className="rtq-spin" aria-hidden /> Loading the attached CSV{emailPreview.csvCount === 1 ? '' : 's'}…
                </p>
              ) : (
                csvPreviews.map(preview => {
                  if (preview.error && !preview.rawCsvText) {
                    return (
                      <p key={preview.key} className="pf-split-problem" role="alert">
                        {preview.fileName}: {preview.error}
                      </p>
                    );
                  }
                  const requestedSpecies = emailPreview.exportOptions?.requestedSpeciesEntries?.map(entry => entry.species) ?? [];
                  const csvText = preview.useRawFile
                    ? prepareTimberSupplierCsvText(preview.rawCsvText || '', requestedSpecies, {
                        trueDimensions: preview.trueDimensions,
                      })
                    : buildTimberSupplierCsvText({ nodeSets: preview.nodeSets }, emailPreview.exportOptions);
                  return (
                    <TimberCsvPreviewTable
                      key={preview.key}
                      sourceFileName={timberRfqEmailAttachmentFilename(preview.fileName)}
                      csvText={csvText}
                      canPublish
                    />
                  );
                })
              )}
            </div>
          </div>
        ) : null}
      </section>
      {step3Problem ? <p className="pf-split-problem">{step3Problem}</p> : null}
    </>
  );

  const stepLabel = step === 1 ? 'Scope' : step === 2 ? 'Suppliers' : 'Review & send';
  const problem = step === 1 ? step1Problem : step === 2 ? step2Problem : step3Problem;
  const canAdvance = !busy && !problem;
  const sendLabel = `Send to ${selectedOrgIds.length} supplier${selectedOrgIds.length !== 1 ? 's' : ''}`;

  const modal = (
    <div
      className="pf-modal-overlay rtq-overlay"
      role="presentation"
      onMouseDown={e => {
        if (e.target === e.currentTarget && !busy) onCancel();
      }}
    >
      <div className="pf-modal pf-modal--split pf-modal--structures rtq-modal" role="dialog" aria-modal="true" aria-labelledby="rtq-title" onMouseDown={e => e.stopPropagation()}>
        <div className="pf-split-modal-head">
          <h3 className="pf-modal-title" id="rtq-title">
            <Send size={16} aria-hidden /> Request timber quotes
            <span className="pf-structures-step">
              Step {step} of 3 · {stepLabel}
            </span>
          </h3>
          <button type="button" className="pf-split-modal-close" disabled={busy} aria-label="Close" onClick={onCancel}>
            <X size={16} aria-hidden />
          </button>
        </div>

        <div className="rtq-body">{step === 1 ? renderStep1() : step === 2 ? renderStep2() : renderStep3()}</div>

        <div className="pf-modal-actions">
          {step > 1 ? (
            <button type="button" className="pf-modal-cancel" disabled={busy} onClick={() => setStep(s => (s === 3 ? 2 : 1))}>
              <ArrowLeft size={13} aria-hidden /> Back
            </button>
          ) : null}
          <button type="button" className="pf-modal-cancel" disabled={busy} onClick={onCancel}>
            Not now
          </button>
          {step < 3 ? (
            <button type="button" className="pf-upload-btn pf-upload-btn--accent" disabled={!canAdvance} onClick={() => setStep(s => (s === 1 ? 2 : 3))}>
              {step === 1 ? 'Next: suppliers' : 'Next: review'} <ArrowRight size={14} aria-hidden />
            </button>
          ) : (
            <button type="button" className="pf-upload-btn pf-upload-btn--accent" disabled={!canAdvance} onClick={send}>
              {busy ? (
                <>
                  <Loader2 size={14} className="pf-spin" /> Sending…
                </>
              ) : (
                <>
                  <Send size={14} aria-hidden /> {sendLabel}
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default RequestTimberQuotesWizard;
